import { useEffect, useState } from "react";
import { StreamChat, Channel as StreamChannel } from "stream-chat";
import {
  Channel,
  MessageList,
  MessageInput,
  Window,
  Thread,
} from "stream-chat-react";
import WorkspaceInfoCard from "./WorkspaceInfoCard";

interface Conversation {
  id: number;
  streamChannelId: string;
  bookingId: number;
  workspaceId: number;
  workspaceTitle: string;
  workspaceImageUrl: string | null;
  otherUserId: number;
  otherUserName: string;
  otherUserImage: string | null;
  lastMessageAt: string | null;
}

interface ConversationViewProps {
  conversation: Conversation;
  client: StreamChat;
  onBack: () => void;
}

export default function ConversationView({
  conversation,
  client,
  onBack,
}: ConversationViewProps) {
  const [channel, setChannel] = useState<StreamChannel | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadChannel = async () => {
      setIsLoading(true);
      try {
        const newChannel = client.channel("messaging", conversation.streamChannelId);
        await newChannel.watch();
        setChannel(newChannel);
      } catch (error) {
        console.error("Error loading channel:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadChannel();

    return () => {
      if (channel) {
        channel.stopWatching();
      }
    };
  }, [conversation.streamChannelId, client]);

  if (isLoading || !channel) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--secondary)]"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with back button (mobile) */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200 md:hidden">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <span className="font-medium">{conversation.otherUserName}</span>
      </div>

      {/* Workspace Info Card */}
      <WorkspaceInfoCard
        workspaceId={conversation.workspaceId}
        workspaceTitle={conversation.workspaceTitle}
        workspaceImageUrl={conversation.workspaceImageUrl}
        bookingId={conversation.bookingId}
      />

      {/* Chat Area */}
      <div className="flex-1 overflow-hidden">
        <Channel channel={channel}>
          <Window>
            <MessageList />
            <MessageInput />
          </Window>
          <Thread />
        </Channel>
      </div>
    </div>
  );
}
